/**
 * Memory: WEB list surface (M5.2). Grouped, searchable list of the durable
 * Career Memory graph - list-first on purpose. At real-world node counts a
 * node-graph rendering conveyed nothing (a few cluster bubbles floating in an
 * empty card), so web shows the memories themselves; the native panel keeps
 * its radial portrait (MemoryGraphPortrait.tsx).
 *
 * ⚠️ WEB-ONLY (DOM rendering). It lives in a `.web.tsx` file and is imported
 * ONLY from other `.web.tsx` files (the memory settings panels). NEVER import
 * it from a shared/native module.
 *
 * UX:
 * - Search + type chips + a time window drive the grouped list; opening a row
 *   discloses its detail inline (full sublabel, connections, revoke, forget).
 * - Cutting an AGENT_HAS_ACCESS edge revokes it (confirm); deleting a node
 *   soft-deletes with an Undo bar; a cap teaser + upgrade CTA shows when the
 *   plan truncated the graph; a refresh chip re-runs the server-side graph
 *   projection (parity with the native panel's rebuild button).
 *
 * Visual language: the `.tabbio-jobs-toolbar` / `.tabbio-search` /
 * `.tabbio-chip` vocabulary from /jobs plus the `.tabbio-mem-*` classes in
 * global.css - no ad-hoc inline surfaces, radii or hex colours here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GraphEdge, GraphNode, GraphNodeType } from '@tabbio/types';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { confirm } from '../../lib/alert.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import {
  deleteMemoryNode,
  fetchMemoryGraph,
  NODE_TYPE_ORDER,
  rebuildMemoryGraph,
  restoreMemoryNode,
  revokeAgentEdge,
  type MemoryGraphResult,
} from '../../lib/memory-graph-api';

const AGENT_ACCESS_EDGE = 'AGENT_HAS_ACCESS';

interface UndoState {
  nodeId: string;
  label: string;
}

const TIME_WINDOWS = [
  { key: 'all', days: null },
  { key: 'd7', days: 7 },
  { key: 'd30', days: 30 },
  { key: 'd365', days: 365 },
] as const;
type TimeWindowKey = (typeof TIME_WINDOWS)[number]['key'];

/** Borderless 13/500 text button - same treatment as the jobs filter row's
 * "Clear all". */
const textBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 500,
  padding: '0 4px',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
};

export function MemoryList() {
  const { t } = useTranslation('common');
  const upgradeModal = useUpgradeModal();

  const [data, setData] = useState<MemoryGraphResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeTypes, setActiveTypes] = useState<Set<GraphNodeType>>(new Set());
  const [timeWindow, setTimeWindow] = useState<TimeWindowKey>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoState | null>(null);
  const [rebuilding, setRebuilding] = useState(false);

  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nodeById = useMemo(() => {
    const map = new Map<string, GraphNode>();
    for (const node of data?.nodes ?? []) map.set(node.id, node);
    return map;
  }, [data]);

  const edgesByNode = useMemo(() => {
    const map = new Map<string, GraphEdge[]>();
    const push = (key: string, edge: GraphEdge) => {
      const list = map.get(key);
      if (list) list.push(edge);
      else map.set(key, [edge]);
    };
    for (const edge of data?.edges ?? []) {
      push(edge.sourceNodeId, edge);
      push(edge.targetNodeId, edge);
    }
    return map;
  }, [data]);

  const typeCounts = useMemo(() => {
    const counts = new Map<GraphNodeType, number>();
    for (const node of data?.nodes ?? []) {
      counts.set(node.nodeType, (counts.get(node.nodeType) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  // ── Data load ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since =
        TIME_WINDOWS.find((w) => w.key === timeWindow)?.days != null
          ? new Date(
              Date.now() -
                (TIME_WINDOWS.find((w) => w.key === timeWindow)!.days as number) *
                  86_400_000,
            ).toISOString()
          : undefined;
      const result = await fetchMemoryGraph({ since });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.memoryGraph.loadError'));
    } finally {
      setLoading(false);
    }
  }, [timeWindow, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  // Materialized graph nodes only auto-rebuild once, from empty - the refresh
  // chip re-runs the server projection on demand (parity with native).
  const handleRebuild = useCallback(async () => {
    if (rebuilding) return;
    setRebuilding(true);
    try {
      await rebuildMemoryGraph();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.memoryGraph.rebuildError'));
    } finally {
      setRebuilding(false);
    }
  }, [rebuilding, load, t]);

  const handleCutEdge = useCallback(
    async (edgeId: string) => {
      const edge = data?.edges.find((e) => e.id === edgeId);
      if (!edge || edge.edgeType !== AGENT_ACCESS_EDGE) return;
      // AGENT_HAS_ACCESS: source is the agent node.
      const agentLabel = nodeById.get(edge.sourceNodeId)?.label ?? '';
      const ok = await confirm({
        title: t('settings.memoryGraph.revokeTitle'),
        message: t('settings.memoryGraph.revokeMessage', { name: agentLabel }),
        confirmLabel: t('settings.memoryGraph.revokeConfirm'),
        cancelLabel: t('actions.cancel'),
        destructive: true,
      });
      if (!ok) return;
      try {
        await revokeAgentEdge(edgeId);
        setData((prev) =>
          prev ? { ...prev, edges: prev.edges.filter((e) => e.id !== edgeId) } : prev,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.memoryGraph.revokeError'));
      }
    },
    [data, nodeById, t],
  );

  const clearUndo = useCallback(() => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
    setUndo(null);
  }, []);

  const handleDeleteNode = useCallback(
    async (nodeId: string) => {
      const node = nodeById.get(nodeId);
      if (!node) return;
      const ok = await confirm({
        title: t('settings.memoryGraph.deleteTitle'),
        message: t('settings.memoryGraph.deleteMessage', { name: node.label }),
        confirmLabel: t('actions.delete'),
        cancelLabel: t('actions.cancel'),
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteMemoryNode(nodeId);
        setExpandedId(null);
        setData((prev) =>
          prev
            ? {
                ...prev,
                nodes: prev.nodes.filter((n) => n.id !== nodeId),
                edges: prev.edges.filter(
                  (e) => e.sourceNodeId !== nodeId && e.targetNodeId !== nodeId,
                ),
              }
            : prev,
        );
        setUndo({ nodeId, label: node.label });
        if (undoTimer.current) clearTimeout(undoTimer.current);
        undoTimer.current = setTimeout(() => setUndo(null), 6500);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.memoryGraph.deleteError'));
      }
    },
    [nodeById, t],
  );

  const handleUndo = useCallback(async () => {
    if (!undo) return;
    const nodeId = undo.nodeId;
    clearUndo();
    try {
      await restoreMemoryNode(nodeId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.memoryGraph.restoreError'));
    }
  }, [undo, clearUndo, load, t]);

  const toggleType = useCallback((type: GraphNodeType) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  useEffect(() => () => clearUndo(), [clearUndo]);

  const presentTypes = useMemo(
    () => NODE_TYPE_ORDER.filter((type) => typeCounts.has(type)),
    [typeCounts],
  );
  const meta = data?.meta;

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return presentTypes
      .filter((type) => activeTypes.size === 0 || activeTypes.has(type))
      .map((type) => ({
        type,
        items: (data?.nodes ?? []).filter(
          (n) =>
            n.nodeType === type &&
            (!q ||
              n.label.toLowerCase().includes(q) ||
              (n.sublabel ?? '').toLowerCase().includes(q)),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [data, presentTypes, activeTypes, search]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Cap teaser: only when truncated AND not already on extended memory. */}
      {meta?.truncated && !meta.extendedMemory ? (
        <div className="tabbio-mem-banner">
          <MaterialSymbol
            name="hub"
            size={20}
            style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
              {t('settings.memoryGraph.capTitle', {
                shown: (meta.cap ?? data?.nodes.length ?? 0).toLocaleString(),
                total: meta.nodeCount.toLocaleString(),
              })}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {t('settings.memoryGraph.capSubtitle')}
            </div>
          </div>
          <button
            type="button"
            className="tabbio-mem-cta"
            onClick={() => upgradeModal.open('individual')}
          >
            {t('settings.memoryGraph.capCta')}
          </button>
        </div>
      ) : null}

      {/* Toolbar: search + refresh, then filter chip rows - the same box
          /jobs uses, so the two pages read as one system. */}
      <div className="tabbio-jobs-toolbar" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            className="tabbio-search"
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              height: 48,
              borderRadius: 9999,
              paddingInline: 18,
              border: '1px solid var(--input-border)',
              background: 'var(--bg)',
            }}
          >
            <MaterialSymbol
              name="search"
              size={20}
              style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('settings.memoryGraph.searchPlaceholder')}
              dir="auto"
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                color: 'var(--text-primary)',
                fontSize: 15,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            {search.length > 0 ? (
              <button
                type="button"
                className="tabbio-icon-btn"
                onClick={() => setSearch('')}
                aria-label={t('actions.clear')}
              >
                <MaterialSymbol name="close" size={18} />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className="tabbio-chip"
            onClick={() => void handleRebuild()}
            disabled={rebuilding || loading}
            aria-label={t('settings.memoryGraph.rebuild')}
            title={t('settings.memoryGraph.rebuild')}
            style={{ width: 40, height: 40, padding: 0, justifyContent: 'center', flexShrink: 0 }}
          >
            <MaterialSymbol name="refresh" size={18} />
          </button>
        </div>

        <div className="tabbio-jobs-toolbar__divider" />

        <div className="tabbio-jobs-filter-row" style={{ display: 'flex', gap: 8 }}>
          {TIME_WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              className={timeWindow === w.key ? 'tabbio-chip active' : 'tabbio-chip'}
              onClick={() => setTimeWindow(w.key)}
            >
              {t(`settings.memoryGraph.window.${w.key}`)}
            </button>
          ))}
        </div>

        {presentTypes.length > 0 ? (
          <div
            className="tabbio-jobs-filter-row"
            style={{ display: 'flex', gap: 8, marginTop: 8 }}
          >
            {presentTypes.map((type) => (
              <button
                key={type}
                type="button"
                className={activeTypes.has(type) ? 'tabbio-chip active' : 'tabbio-chip'}
                onClick={() => toggleType(type)}
              >
                {t(`settings.memoryGraph.nodeTypes.${type}`)} · {typeCounts.get(type)}
              </button>
            ))}
            {activeTypes.size > 0 ? (
              <button
                type="button"
                onClick={() => setActiveTypes(new Set())}
                style={textBtnStyle}
              >
                {t('settings.memoryGraph.clearFilters')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Undo bar: directly under the toolbar so a deletion low in the list
          still surfaces its Undo within view. */}
      {undo ? (
        <div className="tabbio-mem-banner">
          <span
            style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-primary)' }}
            dir="auto"
          >
            {t('settings.memoryGraph.deletedToast', { name: undo.label })}
          </span>
          <button
            type="button"
            onClick={() => void handleUndo()}
            style={{
              border: 0,
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              flexShrink: 0,
            }}
          >
            {t('actions.undo')}
          </button>
        </div>
      ) : null}

      {/* Content: grouped list, or a quiet placeholder state. */}
      {loading ? (
        <div className="tabbio-mem-surface tabbio-mem-surface--center">
          <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {t('settings.memoryGraph.loading')}
          </span>
        </div>
      ) : error ? (
        <div className="tabbio-mem-surface tabbio-mem-surface--center">
          <span style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center' }}>
            {error}
          </span>
        </div>
      ) : (data?.nodes.length ?? 0) === 0 || grouped.length === 0 ? (
        <div className="tabbio-mem-surface tabbio-mem-surface--center">
          <span style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center' }}>
            {t('settings.memoryGraph.empty')}
          </span>
        </div>
      ) : (
        <div>
          {grouped.map((group) => (
            <section key={group.type}>
              <div className="tabbio-mem-group-label">
                <span>{t(`settings.memoryGraph.nodeTypes.${group.type}`)}</span>
                <span className="tabbio-menu-badge">{group.items.length}</span>
              </div>
              {group.items.map((node) => (
                <MemoryItem
                  key={node.id}
                  node={node}
                  edges={edgesByNode.get(node.id) ?? []}
                  nodeById={nodeById}
                  expanded={expandedId === node.id}
                  onToggle={() =>
                    setExpandedId((prev) => (prev === node.id ? null : node.id))
                  }
                  onDelete={() => void handleDeleteNode(node.id)}
                  onCutEdge={(edgeId) => void handleCutEdge(edgeId)}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Small building blocks ─────────────────────────────────────────────────────

function MemoryItem({
  node,
  edges,
  nodeById,
  expanded,
  onToggle,
  onDelete,
  onCutEdge,
}: {
  node: GraphNode;
  edges: GraphEdge[];
  nodeById: Map<string, GraphNode>;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onCutEdge: (edgeId: string) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="tabbio-mem-item" data-expanded={expanded ? 'true' : undefined}>
      <button
        type="button"
        className="tabbio-mem-item-btn"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="tabbio-mem-item-body">
          <span className="tabbio-mem-item-title" dir="auto">
            {node.label}
          </span>
          {!expanded && node.sublabel ? (
            <span className="tabbio-mem-item-sub" dir="auto">
              {node.sublabel}
            </span>
          ) : null}
        </span>
        <MaterialSymbol name="expand_more" size={20} className="tabbio-mem-item-chevron" />
      </button>

      {expanded ? (
        <div className="tabbio-mem-item-detail">
          {node.sublabel ? (
            <div
              style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}
              dir="auto"
            >
              {node.sublabel}
            </div>
          ) : null}

          <div style={{ alignSelf: 'stretch' }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text-secondary)',
                marginBottom: 2,
              }}
            >
              {t('settings.memoryGraph.connections', { count: edges.length })}
            </div>
            {edges.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                {t('settings.memoryGraph.noConnections')}
              </div>
            ) : (
              edges.slice(0, 20).map((edge) => {
                const otherId =
                  edge.sourceNodeId === node.id ? edge.targetNodeId : edge.sourceNodeId;
                const other = nodeById.get(otherId);
                const isAgentAccess = edge.edgeType === AGENT_ACCESS_EDGE;
                return (
                  <div key={edge.id} className="tabbio-mem-edge-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        dir="auto"
                      >
                        {other?.label ?? otherId}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {t(`settings.memoryGraph.edgeTypes.${edge.edgeType}`, {
                          defaultValue: edge.edgeType,
                        })}
                      </div>
                    </div>
                    {isAgentAccess ? (
                      <button
                        type="button"
                        className="tabbio-icon-btn"
                        onClick={() => onCutEdge(edge.id)}
                        aria-label={t('settings.memoryGraph.revokeConfirm')}
                        title={t('settings.memoryGraph.revokeConfirm')}
                      >
                        <MaterialSymbol name="link_off" size={16} />
                      </button>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          {node.nodeType !== 'cv' ? (
            <button type="button" className="tabbio-chip" onClick={onDelete}>
              <MaterialSymbol name="delete" size={16} />
              {t('settings.memoryGraph.deleteNode')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
